import { z } from 'zod';
import { PLANS } from './modules.js';
/**
 * APIs und Webhooks (KON-186 bis KON-194): Verbindungen eines Mandanten zu Diensten außerhalb von Wirkia. Jede Verbindung hat Einstellungen, die der Mandant selbst
 * einträgt oder über eine Anmeldung beim Dienst (OAuth) erteilt, und Geheimnisse (Passwörter, Schlüssel, Tokens), die verschlüsselt liegen und nie zurückgegeben werden.
 */
export const INTEGRATION_KEYS = ['email', 'stripe', 'paypal', 'facebook', 'instagram', 'linkedin', 'tiktok'];
export const CAPABILITY_LABELS = {
    send_mail: 'E-Mails aus Wirkia versenden',
    payment_links: 'Zahlungslinks auf Rechnungen, Zahlungseingang automatisch verbuchen',
    publish_posts: 'Beiträge veröffentlichen',
    receive_leads: 'Anfragen aus Kontaktformularen empfangen',
    receive_messages: 'Nachrichten empfangen und beantworten',
};
export const INTEGRATIONS = [
    { key: 'email', label: 'E-Mail-Versand', description: 'Das Postfach Ihres Unternehmens, über das Wirkia Angebote, Newsletter und Rechnungen verschickt. Ohne diese Einrichtung versendet Wirkia keine E-Mails in Ihrem Namen.', plan: 'basic', capabilities: ['send_mail'] },
    { key: 'stripe', label: 'Stripe', description: 'Ihr Stripe-Konto: Kunden bezahlen eine Rechnung per Karte oder Lastschrift über einen Link, der Zahlungseingang wird auf der Rechnung verbucht.', plan: 'standard', capabilities: ['payment_links'] },
    { key: 'paypal', label: 'PayPal', description: 'Ihr PayPal-Geschäftskonto: Kunden bezahlen eine Rechnung über einen Link, der Zahlungseingang wird auf der Rechnung verbucht.', plan: 'standard', capabilities: ['payment_links'] },
    { key: 'facebook', label: 'Facebook', description: 'Ihre Facebook-Seite über Ihre eigene Meta-App: Beiträge veröffentlichen, Anfragen aus Lead-Formularen und Nachrichten aus dem Messenger in Wirkia empfangen und beantworten.', plan: 'premium', capabilities: ['publish_posts', 'receive_leads', 'receive_messages'] },
    { key: 'instagram', label: 'Instagram', description: 'Ihr Instagram-Profikonto über Ihre eigene Meta-App: Beiträge mit Bildern veröffentlichen, sofort oder geplant. Das Konto muss mit Ihrer Facebook-Seite verknüpft sein.', plan: 'premium', capabilities: ['publish_posts'] },
    { key: 'linkedin', label: 'LinkedIn', description: 'Ihre LinkedIn-Unternehmensseite über Ihre eigene LinkedIn-App: Beiträge mit Text, Bildern und Link veröffentlichen. LinkedIn bietet für Nachrichten und Formulare keine freie Schnittstelle.', plan: 'premium', capabilities: ['publish_posts'] },
    { key: 'tiktok', label: 'TikTok', description: 'Ihr TikTok-Konto über Ihre eigene TikTok-App: Videos und Fotobeiträge veröffentlichen. TikTok bietet für Nachrichten und Formulare keine freie Schnittstelle.', plan: 'premium', capabilities: ['publish_posts'] },
];
export const integrationDefinition = (key) => INTEGRATIONS.find((i) => i.key === key);
export const isIntegrationKey = (v) => INTEGRATION_KEYS.includes(v);
/** Darf ein Mandant mit diesem Paket die Verbindung nutzen? */
export function integrationAvailable(plan, key) {
    return PLANS.indexOf(plan) >= PLANS.indexOf(integrationDefinition(key).plan);
}
export const INTEGRATION_STATUS_LABELS = { not_connected: 'Nicht verbunden', connected: 'Verbunden', error: 'Fehler' };
// --- E-Mail -------------------------------------------------------------------------
export const EMAIL_PORTS = [465, 587, 2525, 25];
export const EMAIL_SECURITY = ['ssl', 'starttls', 'none'];
export const EMAIL_SECURITY_LABELS = { ssl: 'SSL/TLS (Port 465)', starttls: 'STARTTLS (Port 587)', none: 'Unverschlüsselt (nur Entwicklung)' };
const hostname = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Server fehlt')
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/, 'Bitte nur den Servernamen angeben, zum Beispiel smtp.ionos.de')
    .refine((v) => !v.includes('..') && v.includes('.'), 'Bitte den vollständigen Servernamen angeben, zum Beispiel smtp.ionos.de');
export const emailSettingsInput = z
    .object({
    host: hostname,
    /** Im Betrieb prüft der Server zusätzlich, dass es einer der Ports aus EMAIL_PORTS ist (in Entwicklung und Tests ist jeder erlaubt) */
    port: z.number('Bitte einen Port angeben').int().min(1, 'Bitte einen Port angeben').max(65535, 'Ungültiger Port'),
    security: z.enum(EMAIL_SECURITY, 'Bitte die Verschlüsselung wählen'),
    user: z.string().trim().min(1, 'Benutzername fehlt').max(200),
    /** Fehlt das Passwort, bleibt das gespeicherte erhalten (nur beim Ändern einer vorhandenen Einrichtung) */
    password: z.string().min(1, 'Passwort fehlt').max(200).optional(),
    fromName: z.string().trim().max(80).optional().transform((v) => v || undefined),
    fromAddress: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige Absenderadresse angeben')),
    replyTo: z.string().trim().toLowerCase().max(200).optional().transform((v) => v || undefined).pipe(z.email('Bitte eine gültige Antwortadresse angeben').optional()),
})
    .strict();
// --- Stripe und PayPal des Mandanten, Zahlungslinks auf Rechnungen (KON-189) -------------------------------------------------------------------------
export const PAYMENT_PROVIDERS = ['stripe', 'paypal'];
/** Stripe: geheimer oder eingeschränkter Schlüssel (sk_… oder rk_…) und das Signaturgeheimnis des Webhooks (whsec_…). Beim ersten Speichern genügt der Schlüssel, das Webhook-Geheimnis folgt. */
export const stripeSettingsInput = z
    .object({
    secretKey: z
        .string()
        .trim()
        .regex(/^(sk|rk)_(test|live)_[A-Za-z0-9]{10,}$/, 'Das ist kein Stripe-Schlüssel. Er beginnt mit sk_live_, sk_test_, rk_live_ oder rk_test_.')
        .optional(),
    webhookSecret: z.string().trim().regex(/^whsec_[A-Za-z0-9]{10,}$/, 'Das Signaturgeheimnis beginnt mit whsec_').optional(),
})
    .strict()
    .refine((v) => v.secretKey !== undefined || v.webhookSecret !== undefined, 'Bitte einen Schlüssel oder das Signaturgeheimnis angeben');
export const PAYPAL_ENVIRONMENTS = ['sandbox', 'live'];
export const paypalSettingsInput = z
    .object({
    environment: z.enum(PAYPAL_ENVIRONMENTS, 'Bitte Test (Sandbox) oder Live wählen').optional(),
    clientId: z.string().trim().min(10, 'Client-ID fehlt').max(200).optional(),
    secret: z.string().trim().min(10, 'Secret fehlt').max(300).optional(),
    webhookId: z.string().trim().min(5, 'Webhook-ID fehlt').max(100).optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Bitte Angaben machen');
/**
 * Wohin der Kunde nach dem Bezahlen kommt (Einrichtung → APIs und Webhooks, gilt für Stripe und PayPal). Ein Mandant mit eigener Webseite trägt hier seine Seiten ein, zum Beispiel
 * „https://www.beispiel.de/danke“. Ohne Angabe landet der Kunde auf der neutralen Seite von Wirkia. In der Adresse steht {rechnung} für die Rechnungsnummer, damit die Seite sie anzeigen kann.
 */
export const PAYMENT_RETURN_PLACEHOLDER = '{rechnung}';
const returnUrl = z
    .string()
    .trim()
    .max(500, 'Die Adresse ist zu lang.')
    .nullable()
    .transform((v) => (v === null || v === '' ? null : v))
    .refine((v) => {
    if (v === null)
        return true;
    const probe = v.split(PAYMENT_RETURN_PLACEHOLDER).join('RE-1');
    // https, ein Rechnername mit Punkt und keine Zugangsdaten in der Adresse
    return z.url().safeParse(probe).success && /^https:\/\/[^/?#@\s]+\.[^/?#@\s]+/i.test(probe);
}, 'Bitte geben Sie eine vollständige Adresse mit https:// an, zum Beispiel https://www.beispiel.de/danke.');
export const paymentReturnInput = z.object({ successUrl: returnUrl, cancelUrl: returnUrl }).strict();
export const paymentLinkInput = z.object({ provider: z.enum(PAYMENT_PROVIDERS, 'Bitte einen Zahlungsdienst wählen') }).strict();
export const sendPaymentLinkInput = z
    .object({ to: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige E-Mail-Adresse angeben')).optional() })
    .strict();
export const PAYMENT_LINK_STATUS_LABELS = { open: 'Offen', paid: 'Bezahlt', cancelled: 'Zurückgezogen' };
// --- Soziale Netzwerke: Facebook, LinkedIn, TikTok (KON-190 bis KON-193) ---------------------------------------------------------------------------------
export const SOCIAL_PROVIDERS = ['facebook', 'instagram', 'linkedin', 'tiktok'];
export const SOCIAL_LABELS = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', tiktok: 'TikTok' };
export const isSocialProvider = (v) => SOCIAL_PROVIDERS.includes(v);
/** App-Daten speichern. Beim ersten Mal sind ID und Geheimnis Pflicht, danach bleibt ein leeres Geheimnis unverändert. */
export const socialAppInput = z
    .object({
    clientId: z.string().trim().min(3, 'Die App-ID ist zu kurz').max(100, 'Die App-ID ist zu lang').regex(/^[A-Za-z0-9._-]+$/, 'Die App-ID besteht nur aus Buchstaben, Ziffern, Punkt, Bindestrich und Unterstrich').optional(),
    clientSecret: z.string().trim().min(8, 'Das App-Geheimnis ist zu kurz').max(200, 'Das App-Geheimnis ist zu lang').regex(/^\S+$/, 'Das App-Geheimnis enthält keine Leerzeichen').optional(),
    /** Nur Instagram: App-ID und Geheimnis von der Facebook-Verbindung übernehmen (dieselbe Meta-App) */
    sameAsFacebook: z.boolean().optional(),
    /** Nur TikTok */
    audited: z.boolean().optional(),
    verificationFileName: z
        .string()
        .trim()
        .max(100)
        .regex(/^tiktok[A-Za-z0-9_-]{1,80}\.txt$/, 'Der Dateiname der Prüfdatei beginnt mit „tiktok“ und endet auf „.txt“')
        .optional()
        .or(z.literal('').transform(() => undefined)),
    verificationContent: z.string().trim().max(500, 'Der Inhalt der Prüfdatei ist zu lang').optional(),
})
    .strict();
export const socialAccountInput = z.object({ accountId: z.string().trim().min(1, 'Bitte eine Seite wählen').max(100).regex(/^[A-Za-z0-9_-]+$/, 'Ungültige Kennung') }).strict();
/**
 * Beiträge (KON-193, KON-269): Text, Hashtags, Bilder und Link, für TikTok wahlweise ein Video von einer Internetadresse. Ein Beitrag geht sofort hinaus oder zu einem geplanten Zeitpunkt.
 * Die Grenzen richten sich nach dem strengsten Netzwerk: Instagram nimmt höchstens 10 Bilder je Beitrag, 30 Hashtags und 2200 Zeichen.
 */
export const SOCIAL_POST_LIMITS = { text: 3000, tiktokTitle: 2200, instagramCaption: 2200, tiktokPhotoTitle: 90, tags: 30, tagLength: 50, images: 10 };
/** Bilder für Beiträge: höchstens 8 MB, JPG oder PNG. Instagram und TikTok nehmen nur JPG an. */
export const SOCIAL_IMAGE_LIMITS = { maxBytes: 8 * 1024 * 1024 };
export const SOCIAL_IMAGE_TYPES = ['image/jpeg', 'image/png'];
/** Netzwerke, die nur JPG-Bilder annehmen */
export const SOCIAL_JPEG_ONLY = ['instagram', 'tiktok'];
/** Ein geplanter Beitrag darf höchstens so weit in der Zukunft liegen */
export const SOCIAL_SCHEDULE_MAX_DAYS = 365;
/** Ein Hashtag ohne Raute: Buchstaben, Ziffern und Unterstrich, wie ihn alle vier Netzwerke verstehen. Gibt null zurück, wenn nichts Gültiges übrig bleibt. */
export function normalizeHashtag(raw) {
    const tag = raw.trim().replace(/^#+/, '');
    return tag && tag.length <= SOCIAL_POST_LIMITS.tagLength && /^[\p{L}\p{N}_]+$/u.test(tag) ? tag : null;
}
/** Zerlegt eine Eingabe wie „#herbst wein, Weinfest“ in Hashtags ohne Raute, ohne Doppelte (Groß- und Kleinschreibung zählt nicht) */
export function parseHashtags(raw) {
    const tags = [];
    const invalid = [];
    for (const part of raw.split(/[\s,;]+/).filter(Boolean)) {
        const tag = normalizeHashtag(part);
        if (!tag)
            invalid.push(part);
        else if (!tags.some((t) => t.toLowerCase() === tag.toLowerCase()))
            tags.push(tag);
    }
    return { tags, invalid };
}
/**
 * Der Text, wie er bei Facebook, Instagram und TikTok erscheint: Text, bei Bildern der Link (eine Linkvorschau gibt es neben Bildern nicht) und die Hashtags in einer eigenen Zeile.
 * LinkedIn braucht eine eigene Auszeichnung für Hashtags und baut den Text selbst (social-apis.ts).
 */
export function composeSocialText(p) {
    const parts = [p.text?.trim() ?? '', p.linkInText && p.link ? p.link : '', (p.tags ?? []).map((t) => `#${t}`).join(' ')];
    return parts.filter(Boolean).join('\n\n');
}
const https = (message) => z.string().trim().max(500).url(message).refine((u) => /^https:\/\//.test(u), 'Die Adresse muss mit https:// beginnen');
const socialPostFields = z
    .object({
    text: z.string().trim().max(SOCIAL_POST_LIMITS.text, `Höchstens ${SOCIAL_POST_LIMITS.text} Zeichen`).optional().transform((v) => v || undefined),
    link: https('Bitte eine vollständige Internetadresse angeben, zum Beispiel https://…').optional().or(z.literal('').transform(() => undefined)),
    videoUrl: https('Bitte eine vollständige Internetadresse angeben').optional().or(z.literal('').transform(() => undefined)),
    /** Hashtags ohne Raute. Eine Raute am Anfang entfernt Wirkia. */
    tags: z
        .array(z.string().max(SOCIAL_POST_LIMITS.tagLength + 5))
        .max(SOCIAL_POST_LIMITS.tags, `Höchstens ${SOCIAL_POST_LIMITS.tags} Hashtags, mehr nimmt Instagram nicht an`)
        .default([])
        .transform((list, ctx) => {
        const out = [];
        for (const raw of list) {
            const tag = normalizeHashtag(raw);
            if (!tag) {
                ctx.addIssue({ code: 'custom', message: `„${raw}“ ist kein gültiger Hashtag. Erlaubt sind Buchstaben, Ziffern und der Unterstrich, ohne Leerzeichen.` });
                return z.NEVER;
            }
            if (!out.some((t) => t.toLowerCase() === tag.toLowerCase()))
                out.push(tag);
        }
        return out;
    }),
    /** Bilder, die vorher über POST /marketing/posts/images hochgeladen wurden, in der gewünschten Reihenfolge */
    imageIds: z.array(z.uuid('Ungültiges Bild')).max(SOCIAL_POST_LIMITS.images, `Höchstens ${SOCIAL_POST_LIMITS.images} Bilder je Beitrag`).default([]),
    providers: z.array(z.enum(SOCIAL_PROVIDERS)).min(1, 'Bitte mindestens ein Netzwerk wählen').max(SOCIAL_PROVIDERS.length),
    /** Zeitpunkt der Veröffentlichung. Ohne Angabe geht der Beitrag sofort hinaus. */
    scheduledAt: z.iso.datetime({ offset: true, message: 'Bitte einen gültigen Zeitpunkt angeben' }).optional(),
})
    .strict();
function checkPost(v, ctx) {
    const images = v.imageIds.length > 0;
    if (new Set(v.imageIds).size !== v.imageIds.length)
        ctx.addIssue({ code: 'custom', path: ['imageIds'], message: 'Ein Bild ist doppelt ausgewählt' });
    if (v.providers.includes('facebook') && !v.text && !v.link && !images)
        ctx.addIssue({ code: 'custom', path: ['text'], message: 'Für Facebook braucht der Beitrag einen Text, einen Link oder ein Bild' });
    if (v.providers.includes('instagram') && !images)
        ctx.addIssue({ code: 'custom', path: ['imageIds'], message: 'Für Instagram braucht der Beitrag mindestens ein Bild' });
    if (v.providers.includes('instagram') && composeSocialText({ ...v, linkInText: true }).length > SOCIAL_POST_LIMITS.instagramCaption) {
        ctx.addIssue({ code: 'custom', path: ['text'], message: `Instagram erlaubt höchstens ${SOCIAL_POST_LIMITS.instagramCaption} Zeichen samt Link und Hashtags` });
    }
    if (v.providers.includes('linkedin') && !v.text)
        ctx.addIssue({ code: 'custom', path: ['text'], message: 'Für LinkedIn braucht der Beitrag einen Text' });
    if (v.providers.includes('tiktok')) {
        if (!v.videoUrl && !images)
            ctx.addIssue({ code: 'custom', path: ['videoUrl'], message: 'Für TikTok braucht der Beitrag ein Video oder mindestens ein Bild' });
        if (v.videoUrl && images)
            ctx.addIssue({ code: 'custom', path: ['videoUrl'], message: 'TikTok nimmt entweder ein Video oder Bilder, nicht beides. Entfernen Sie das eine oder wählen Sie TikTok ab.' });
        if (composeSocialText({ ...v, linkInText: true }).length > SOCIAL_POST_LIMITS.tiktokTitle)
            ctx.addIssue({ code: 'custom', path: ['text'], message: `TikTok erlaubt höchstens ${SOCIAL_POST_LIMITS.tiktokTitle} Zeichen samt Hashtags` });
    }
    if (v.scheduledAt) {
        const at = Date.parse(v.scheduledAt);
        if (at < Date.now() + 60_000)
            ctx.addIssue({ code: 'custom', path: ['scheduledAt'], message: 'Der Zeitpunkt muss mindestens eine Minute in der Zukunft liegen' });
        else if (at > Date.now() + SOCIAL_SCHEDULE_MAX_DAYS * 86_400_000)
            ctx.addIssue({ code: 'custom', path: ['scheduledAt'], message: `Ein Beitrag lässt sich höchstens ${SOCIAL_SCHEDULE_MAX_DAYS} Tage im Voraus planen` });
    }
}
/** Neuer Beitrag, sofort oder geplant */
export const socialPostInput = socialPostFields.superRefine(checkPost);
/** Ändern eines geplanten Beitrags: alle Angaben neu, der Zeitpunkt ist Pflicht (sofort veröffentlichen geht über eine eigene Aktion) */
export const socialPostUpdateInput = socialPostFields
    .extend({ scheduledAt: z.iso.datetime({ offset: true, message: 'Bitte einen gültigen Zeitpunkt angeben' }) })
    .superRefine((v, ctx) => checkPost(v, ctx));
export const SOCIAL_POST_STATUS_LABELS = { scheduled: 'Geplant', publishing: 'Wird veröffentlicht', done: 'Veröffentlicht' };
/** Rechnung per E-Mail an den Kunden: PDF im Anhang, wahlweise an eine andere Adresse und mit eigenem Text */
export const sendInvoiceInput = z
    .object({
    to: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige E-Mail-Adresse angeben')).optional(),
    message: z.string().trim().max(2000, 'Höchstens 2000 Zeichen').optional(),
})
    .strict();
//# sourceMappingURL=integrations.js.map